/**
 * DEA Number Management
 *
 * CRUD interface for managing DEA registrations post-setup.
 * Shown in User Settings when the clinician is connected to DoseSpot.
 */

import { useState } from 'react';
import { useProviderBillingInfo } from '@features/user';
import { Add, Close, Delete, Edit, Medication } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { US_STATES } from '@shared/constants/states';
import { useSnackbar } from 'notistack';

import {
  useAddDeaNumberMutation,
  useDeleteDeaNumberMutation,
  useEPrescriptionStatus,
  useGetDeaNumbersQuery,
  useUpdateDeaNumberMutation,
} from '../store';
import type { AddDeaNumberRequest, DeaNumberItem, UpdateDeaNumberRequest } from '../types';

/** DEA number validation: standard, mid-level, and suffix formats */
const DEA_PATTERN =
  /^[A-Za-z]{2}\d{7}(-[A-Za-z0-9]{1,7})?$|^[A-Za-z]\d{8}(-[A-Za-z0-9]{1,7})?$/;

interface DeaFormState {
  deaNumber: string;
  state: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  zipCode: string;
}

const emptyForm: DeaFormState = {
  deaNumber: '',
  state: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  zipCode: '',
};

function formatAddress(item: DeaNumberItem): string {
  const parts = [item.address_line1, item.address_line2, item.city, item.state, item.zip_code]
    .filter(Boolean);
  return parts.join(', ') || '—';
}

export function DEANumberManagement() {
  const { enqueueSnackbar } = useSnackbar();
  const { isUserConnected, isLoading: statusLoading } = useEPrescriptionStatus();
  const providerBillingInfo = useProviderBillingInfo();

  const { data, isLoading: listLoading } = useGetDeaNumbersQuery(undefined, {
    skip: !isUserConnected,
  });
  const [addDeaNumber, { isLoading: isAdding }] = useAddDeaNumberMutation();
  const [updateDeaNumber, { isLoading: isUpdating }] = useUpdateDeaNumberMutation();
  const [deleteDeaNumber, { isLoading: isDeleting }] = useDeleteDeaNumberMutation();

  const deaNumbers = data?.dea_numbers ?? [];
  const isMutating = isAdding || isUpdating || isDeleting;

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DeaNumberItem | null>(null);
  const [form, setForm] = useState<DeaFormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<DeaNumberItem | null>(null);

  // Don't render if user isn't connected to DoseSpot
  if (statusLoading || !isUserConnected) return null;

  const prefillFromProfile = (): DeaFormState => ({
    deaNumber: '',
    state: providerBillingInfo?.state || '',
    addressLine1: providerBillingInfo?.facility_address || '',
    addressLine2: '',
    city: providerBillingInfo?.city || '',
    zipCode: providerBillingInfo?.zip || '',
  });

  const openAddModal = () => {
    setEditingItem(null);
    setForm(prefillFromProfile());
    setFormError(null);
    setModalOpen(true);
  };

  const openEditModal = (item: DeaNumberItem) => {
    setEditingItem(item);
    setForm({
      deaNumber: item.dea_number,
      state: item.state || '',
      addressLine1: item.address_line1 || '',
      addressLine2: item.address_line2 || '',
      city: item.city || '',
      zipCode: item.zip_code || '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
    setFormError(null);
  };

  const handleFormChange =
    (field: keyof DeaFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm(prev => ({ ...prev, [field]: e.target.value }));
      if (formError) setFormError(null);
    };

  const validateForm = (): boolean => {
    if (!form.deaNumber.trim()) {
      setFormError('DEA number is required');
      return false;
    }
    if (!DEA_PATTERN.test(form.deaNumber.trim())) {
      setFormError(
        'DEA number format is invalid. Expected: 2 letters + 7 digits (e.g., AB1234567)'
      );
      return false;
    }
    if (!form.state.trim()) {
      setFormError('State is required');
      return false;
    }
    if (!form.addressLine1.trim()) {
      setFormError('Address is required');
      return false;
    }
    if (!form.city.trim()) {
      setFormError('City is required');
      return false;
    }
    if (!form.zipCode.trim()) {
      setFormError('ZIP code is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload: AddDeaNumberRequest | UpdateDeaNumberRequest = {
      dea_number: form.deaNumber.trim(),
      state: form.state.trim(),
      address_line1: form.addressLine1.trim() || null,
      address_line2: form.addressLine2.trim() || null,
      city: form.city.trim() || null,
      zip_code: form.zipCode.trim() || null,
    };

    try {
      if (editingItem) {
        await updateDeaNumber({
          deaId: editingItem.dea_id,
          updateDeaNumberRequest: payload,
        }).unwrap();
        enqueueSnackbar('DEA number updated', { variant: 'success' });
      } else {
        await addDeaNumber({ addDeaNumberRequest: payload }).unwrap();
        enqueueSnackbar('DEA number added', { variant: 'success' });
      }
      closeModal();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? ((err as { data?: { message?: string } }).data?.message || 'An error occurred')
          : 'An error occurred';
      setFormError(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDeaNumber({ deaId: deleteTarget.dea_id }).unwrap();
      enqueueSnackbar('DEA number removed', { variant: 'success' });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? ((err as { data?: { message?: string } }).data?.message || 'Failed to remove DEA number')
          : 'Failed to remove DEA number';
      enqueueSnackbar(message, { variant: 'error' });
    }
    setDeleteTarget(null);
  };

  return (
    <>
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Medication color="primary" />
          <Typography variant="h6">DEA Numbers</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {listLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={24} />
            <Typography>Loading DEA numbers...</Typography>
          </Box>
        ) : deaNumbers.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No DEA numbers registered.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openAddModal}
              disabled={isMutating}
            >
              Add DEA Number
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" aria-label="DEA numbers">
                <TableHead>
                  <TableRow>
                    <TableCell>DEA Number</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deaNumbers.map(item => (
                    <TableRow key={item.dea_id}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {item.dea_number}
                      </TableCell>
                      <TableCell>{item.state || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatAddress(item)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => openEditModal(item)}
                          disabled={isMutating}
                          aria-label={`Edit DEA number ${item.dea_number}`}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(item)}
                          disabled={isMutating}
                          aria-label={`Delete DEA number ${item.dea_number}`}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={openAddModal}
                disabled={isMutating}
                size="small"
              >
                Add DEA Number
              </Button>
            </Box>
          </>
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth={"sm"}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editingItem ? 'Edit DEA Number' : 'Add DEA Number'}
          <IconButton size="small" onClick={closeModal} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="DEA Number"
              value={form.deaNumber}
              onChange={handleFormChange('deaNumber')}
              required
              placeholder="AB1234567"
              helperText="e.g. AB1234567 or AB1234567-X1"
              disabled={isMutating}
             slotProps={{ htmlInput: { maxLength: 17 } }}/>

            <TextField
              select
              fullWidth
              label="State"
              value={form.state}
              onChange={handleFormChange('state')}
              required
              disabled={isMutating}
            >
              {Object.entries(US_STATES).map(([code, name]) => (
                <MenuItem key={code} value={code}>
                  {code} — {name}
                </MenuItem>
              ))}
            </TextField>

            <Divider />

            <TextField
              fullWidth
              label="Address Line 1"
              value={form.addressLine1}
              onChange={handleFormChange('addressLine1')}
              disabled={isMutating}
              required
            />
            <TextField
              fullWidth
              label="Address Line 2"
              value={form.addressLine2}
              onChange={handleFormChange('addressLine2')}
              disabled={isMutating}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="City"
                value={form.city}
                onChange={handleFormChange('city')}
                disabled={isMutating}
                required
              />
              <TextField
                label="ZIP Code"
                value={form.zipCode}
                onChange={handleFormChange('zipCode')}
                disabled={isMutating}
                required
                sx={{ minWidth: 120 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={isMutating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isMutating}
            startIcon={isMutating ? <CircularProgress size={16} /> : undefined}
          >
            {isMutating ? 'Saving...' : editingItem ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Remove DEA Number</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove DEA number{' '}
            <strong>{deleteTarget?.dea_number}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {isDeleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
